import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import Logo from "components/Logo";
import __ from "utils/i18n";
import ConfigurationModal from "containers/Dashboard/modals/ConfigurationModal";
import { Switch, Route, Redirect } from "react-router-dom";
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { DashboardHeader } from "./components/DashboardHeader";
import DashboardTabbedSection from "./components/dashboardSection/DashboardTabbedSection";
import DashboardSectionTab from "./components/dashboardSection/DashboardSectionTab";
import { setFirstLogin } from "../Login/actions";
import { getConfig } from "config";
import { HelpModal, PaymentLinkModal } from "containers/Dashboard/modals";
import { ImportModal } from "containers/Dashboard/sections/CompraClick/components/Modals";
import MassivePaymentLinkModal from "containers/Dashboard/modals/MassivePaymentLinkModal";
import { useStreamHubNotifications } from "containers/Dashboard/modals/MassivePaymentLinkModal/notifications/useStreamHubNotifications";
import { FloatingNotifications } from "containers/Dashboard/modals/MassivePaymentLinkModal/notifications/FloatingNotifications";
import { tenantId as staticTenantId } from "config/envConfig";
import { ModalContext } from "../../contexts/modalContext";
import { getSections } from "./_shared/getSections";
import {
  BankAccountModal,
  SubsidiariesModal,
  CourierTrackingModal,
  StaticQRCodeModal,
  DocumentationModal,
  ReadersModal,
} from "containers/Dashboard/modals";
import { PermissionsContext } from "contexts/permissionsContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DashboardSection = (props) => {
  if (props.section.component) {
    return React.createElement(props.section.component, props);
  } else if (props.section.tabs) {
    return (
      <DashboardTabbedSection>
        {props.section.tabs.map((tab, index) => (
          <DashboardSectionTab key={index} {...tab} />
        ))}
      </DashboardTabbedSection>
    );
  }
};

const sanitizeBearer = (token) => {
  if (!token || typeof token !== "string") {
    return null;
  }

  const trimmed = token.trim();
  if (!trimmed) {
    return null;
  }

  return /^Bearer\s+/i.test(trimmed)
    ? trimmed.replace(/^Bearer\s+/i, "").trim()
    : trimmed;
};

const sanitizeTenantId = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text.length > 0 ? text : null;
};

export const Dashboard = () => {
  // useState
  const [showFundsModal, setShowFundsModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDocumentationModal, setShowDocumentationModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSubsidiariesModal, setShowSubsidiariesModal] = useState(false);
  const [showCourierTrackingModal, setShowCourierTrackingModal] = useState(false);
  const [showStaticQRCodeModal, setShowStaticQRCodeModal] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMassivePaymentLinkModal, setShowMassivePaymentLinkModal] = useState(false);
  // Campaign IDs
  const [pendingCampaignUuid, setPendingCampaignUuid] = useState(null); // campaña validada en el modal (aún sin confirmar)
  const [processingCampaignUuid, setProcessingCampaignUuid] = useState(null); // campaña en curso (la que sigue la ventana flotante)
  const [isStreamEnabled, setIsStreamEnabled] = useState(false);
  const [, setActiveTenantId] = useState(null);
  const [showProcessingPopup, setShowProcessingPopup] = useState(false);
  
  // Campaign streaming state managed via campaignPanel

  const [deleteModal, setDeleteModal] = useState(false);
  const [showReadersModal, setShowReadersModal] = useState(false);

  // useSelector
  const isAuthenticated = useSelector((state) => state.login.isAuthenticated);
  const isFirstLogin = useSelector((state) => state.login.isFirstLogin);
  const token = useSelector((state) => state.api.token);
  const user = useSelector((state) => state.login.user);
  const buyReaderUrl = useSelector((state) => state.login?.user?.buyReaderURL);

  // useDispatch
  const dispatch = useDispatch();

  // useContext
  const { userPermissions, featureFlags } = useContext(PermissionsContext);
  const isFirstLoginBuyReaderModalEnabled = featureFlags?.includes(
    "first_login_buy_reader_modal"
  );

  const sections = getSections(userPermissions, featureFlags, user);
  // eslint-disable-next-line no-unused-vars
  const tenantHint =
    user?.tenant?.tenantIdentifier ||
    user?.tenant?.tenant_identifier ||
    user?.tenant?.tenantID ||
    user?.tenant?.tenant_id ||
    user?.tenant?.identifier ||
    user?.tenant?.id ||
    user?.tenantId ||
    user?.tenantIdentifier ||
    user?.tenant_identifier ||
    user?.tenantID ||
    user?.tenant_id ||
    user?.tenants?.[0]?.tenantIdentifier ||
    user?.tenants?.[0]?.tenant_identifier ||
    user?.tenants?.[0]?.tenantID ||
    user?.tenants?.[0]?.tenant_id ||
    user?.tenants?.[0]?.id ||
    user?.subsidiaryId ||
    user?.subsidiaryCode ||
    user?.subsidiaryID ||
    staticTenantId ||
    null;
  const {
    connected: _sseConnected, // eslint-disable-line no-unused-vars
    failed: sseFailedCount,
    success: sseSuccessCount,
    completed: metricsCompleted,
    lastError: sseLastError,
    lastEvent,
    connect: connectStreamHub,
    disconnect: disconnectStreamHub,
  } = useStreamHubNotifications();
  const [uiCompleted, setUiCompleted] = useState(false);
  const bareToken = React.useMemo(() => sanitizeBearer(token), [token]);
  const [inactivityDeadline, setInactivityDeadline] = useState(null);

  // Helpers para persistir estado de campaña en sessionStorage (rehidratación)
  const getCampaignStateKey = React.useCallback(
    (tenantId) => `streamhub_campaign_state_${tenantId || staticTenantId || 'unknown'}`,
    []
  );
  const persistCampaignState = React.useCallback((tenantId, state) => {
    try {
      const key = getCampaignStateKey(tenantId);
      sessionStorage.setItem(key, JSON.stringify({ ...state, persistedAt: Date.now() }));
    } catch {}
  }, [getCampaignStateKey]);
  const readPersistedCampaignState = React.useCallback((tenantId) => {
    try {
      const key = getCampaignStateKey(tenantId);
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, [getCampaignStateKey]);
  const clearPersistedCampaignState = React.useCallback((tenantId) => {
    try {
      const key = getCampaignStateKey(tenantId);
      sessionStorage.removeItem(key);
    } catch {}
  }, [getCampaignStateKey]);

  // Función para actualizar datos de la notificación
  const userFundsDefaultValues = {
    title: (user && user.retirementFunds && user.retirementFunds.title) || null,
    data: (user && user.retirementFunds && user.retirementFunds.data) || null,
  };

  const openModal = (keyname) => {
    const modalOptions = {
      FundsModal: () => {
        setShowFundsModal(true);
      },
      ConfigModal: () => {
        setShowConfigModal(true);
      },
      DocumentationModal: () => {
        setShowDocumentationModal(true);
      },
      BuyModal: () => {
        setShowBuyModal(true);
      },
      BankAccountModal: () => {
        setShowBankAccountModal(true);
      },
      HelpModal: () => {
        setShowHelpModal(true);
      },
      SubsidiariesModal: () => {
        setShowSubsidiariesModal(true);
      },
      CourierTrackingModal: () => {
        setShowCourierTrackingModal(true);
      },
      StaticQRCodeModal: () => {
        setShowStaticQRCodeModal(true);
      },
      PaymentLinkModal: () => {
        setShowPaymentLinkModal(true);
      },
      ImportModal: () => {
        setShowImportModal(true);
      },
      ReadersModal: () => {
        setShowReadersModal(true);
      },
      MassivePaymentLinkModal: () => {
        setShowMassivePaymentLinkModal(true);
      },
    };
    
    const modalFunction = modalOptions[keyname];
    if (modalFunction) {
      return modalFunction();
    }
  };
  
  const closeModal = (keyname) => {
    const modalOptions = {
      FundsModal: () => setShowFundsModal(false),
      ConfigModal: () => setShowConfigModal(false),
      DocumentationModal: () => setShowDocumentationModal(false),
      BuyModal: () => setShowBuyModal(false),
      BankAccountModal: () => setShowBankAccountModal(false),
      HelpModal: () => setShowHelpModal(false),
      SubsidiariesModal: () => setShowSubsidiariesModal(false),
      CourierTrackingModal: () => setShowCourierTrackingModal(false),
      StaticQRCodeModal: () => setShowStaticQRCodeModal(false),
      PaymentLinkModal: () => setShowPaymentLinkModal(false),
      ImportModal: () => setShowImportModal(false),
      ReadersModal: () => setShowReadersModal(false),
      MassivePaymentLinkModal: () => setShowMassivePaymentLinkModal(false),
    };
    return modalOptions[keyname]();
  };

  const [campaignTotal, setCampaignTotal] = useState(0);
  const [campaignFileName, setCampaignFileName] = useState(null);
  const [rehydratedSuccess, setRehydratedSuccess] = useState(null);
  const [rehydratedFailed, setRehydratedFailed] = useState(null);

  const handleCampaignSubscribe = ({ campaignUuid, totalRegisters, fileName }) => {
    if (!campaignUuid) {
      return;
    }

    // Guardar datos en el contexto del modal (pendiente de confirmación). No tocar la ventana flotante.
    setPendingCampaignUuid(campaignUuid);
    if (typeof totalRegisters === "number") {
      setCampaignTotal(totalRegisters);
    }
    if (fileName) {
      setCampaignFileName(fileName);
    }
    console.log('[Dashboard] campaign subscribe', {
      campaignUuid,
      totalRegisters,
      fileName,
    });
  };

  const handleCampaignConfirmed = ({ campaignUuid, tenantId, totalRegisters, fileName }) => {
    if (!campaignUuid) {
      return;
    }

    console.log('[Dashboard] campaign confirmed', {
      campaignUuid,
      tenantId,
    });

    // Usar SIEMPRE el tenant de configuración para el tópico/cliente
    const candidateTenant = sanitizeTenantId(staticTenantId);
    if (!candidateTenant) {
      console.warn("[Dashboard] missing tenant identifier from envConfig");
      return;
    }

    // Resetear UI para la nueva campaña a partir de la confirmación
    setUiCompleted(false);
    // Normalizar metadatos: priorizar lo recibido por confirmación, sino usar lo pendiente
    const totalFromConfirm = typeof totalRegisters === "number" ? totalRegisters : campaignTotal;
    const fileFromConfirm = fileName || campaignFileName;
    setCampaignTotal(totalFromConfirm);
    setCampaignFileName(fileFromConfirm);
    // Promover la campaña pendiente a campaña en proceso
    setProcessingCampaignUuid(campaignUuid || pendingCampaignUuid);

    // Persistir estado para rehidratación
    persistCampaignState(candidateTenant, {
      active: true,
      campaignUuid: campaignUuid || pendingCampaignUuid,
      totalRegisters: totalFromConfirm,
      fileName: fileFromConfirm,
    });

    if (!bareToken) {
      console.warn("[Dashboard] missing token for StreamHub connection");
      return;
    }

    connectStreamHub({
      campaignUuid,
      tenantId: candidateTenant,
      token: bareToken,
    });

    setIsStreamEnabled(true);
    setActiveTenantId(candidateTenant);
    // Establecer deadline de inactividad (10 minutos)
    setInactivityDeadline(Date.now() + 10 * 60 * 1000);
  };

  // Reset del flujo de modal. Por defecto no afecta a la campaña en proceso.
  const handleCampaignReset = ({ force = false } = {}) => {
    // Limpiar sólo el contexto pendiente (modal)
    setPendingCampaignUuid(null);
    // Si se fuerza el reset, también se desconecta el stream y se limpia la campaña en proceso
    if (force) {
      disconnectStreamHub();
      setIsStreamEnabled(false);
      setProcessingCampaignUuid(null);
      setActiveTenantId(null);
      setUiCompleted(false);
    }
    console.log('[Dashboard] campaign reset', { force });
  };

  useEffect(() => {
    // Solo reaccionar al completion si el stream actual estaba activo.
    if (!isStreamEnabled) {
      return;
    }
    if (metricsCompleted && processingCampaignUuid) {
      console.log('[Dashboard] campaign completed, enabling new campaigns', {
        campaignUuid: processingCampaignUuid,
      });
      setUiCompleted(true);
      if (!showProcessingPopup) {
        setShowProcessingPopup(true);
      }
      // Desconectar preservando los contadores para mantener el resumen visible
      disconnectStreamHub(true);
      setIsStreamEnabled(false);
      setProcessingCampaignUuid(null);
      setActiveTenantId(null);
      // Persistir snapshot final para poder reabrir después de un refresh si el usuario no la cerró (ack)
      try {
        const tenant = sanitizeTenantId(staticTenantId);
        if (tenant) {
          persistCampaignState(tenant, {
            active: false,
            completed: true,
            acknowledged: false,
            campaignUuid: processingCampaignUuid,
            totalRegisters: campaignTotal,
            fileName: campaignFileName,
            success: sseSuccessCount,
            failed: sseFailedCount,
            finishedAt: Date.now(),
          });
          setRehydratedSuccess(sseSuccessCount);
          setRehydratedFailed(sseFailedCount);
        }
      } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricsCompleted, processingCampaignUuid, disconnectStreamHub, showProcessingPopup, isStreamEnabled, persistCampaignState, campaignTotal, campaignFileName, sseSuccessCount, sseFailedCount]);

  // Bump del deadline al recibir actividad (cualquier evento o cambio de contadores)
  useEffect(() => {
    if (!isStreamEnabled || uiCompleted) return;
    // Cada evento renueva el deadline
    setInactivityDeadline(Date.now() + 10 * 60 * 1000);
  }, [lastEvent, sseSuccessCount, sseFailedCount, isStreamEnabled, uiCompleted]);

  // Watchdog de inactividad: si pasan 10 minutos sin actividad, liberar UI
  useEffect(() => {
    if (!isStreamEnabled || uiCompleted) return;
    const id = setInterval(() => {
      if (inactivityDeadline && Date.now() > inactivityDeadline) {
        console.warn('[Dashboard] inactivity watchdog: releasing active campaign after 10 min without activity');
        disconnectStreamHub();
        setIsStreamEnabled(false);
        setProcessingCampaignUuid(null);
        setActiveTenantId(null);
        setUiCompleted(false);
        setShowProcessingPopup(false);
        setInactivityDeadline(null);
        clearPersistedCampaignState(staticTenantId);
      }
    }, 30000); // check cada 30s
    return () => clearInterval(id);
  }, [inactivityDeadline, isStreamEnabled, uiCompleted, disconnectStreamHub, clearPersistedCampaignState]);
  // Rehidratación: si existe campaña activa persistida, restaurar UI y reconectar SSE
  useEffect(() => {
    const tenant = sanitizeTenantId(staticTenantId);
    if (!tenant) return;
    const persisted = readPersistedCampaignState(tenant);
    if (!persisted) return;

    // Caso A: campaña ya finalizada pero no reconocida por el usuario → mostrar resumen estático
    if (persisted.completed && !persisted.acknowledged) {
      setProcessingCampaignUuid(persisted.campaignUuid || null);
      setCampaignFileName(persisted.fileName || null);
      setCampaignTotal(persisted.totalRegisters || 0);
      setUiCompleted(true);
      setIsStreamEnabled(false);
      setActiveTenantId(tenant);
      setShowProcessingPopup(true);
      setRehydratedSuccess(
        typeof persisted.success === 'number' ? persisted.success : 0,
      );
      setRehydratedFailed(
        typeof persisted.failed === 'number' ? persisted.failed : 0,
      );
      return;
    }

    // Caso B: campaña activa en curso → reabrir y reconectar SSE (pidiendo backlog si no hay lastEventId)
    if (persisted.active && persisted.campaignUuid) {
      setProcessingCampaignUuid(persisted.campaignUuid);
      setCampaignFileName(persisted.fileName || null);
      setCampaignTotal(persisted.totalRegisters || 0);
      setUiCompleted(false);
      setIsStreamEnabled(true);
      setActiveTenantId(tenant);
      setShowProcessingPopup(true);

      if (bareToken) {
        // Pedir backlog desde el inicio de la ventana para cubrir huecos si refrescamos antes de guardar lastEventId
        connectStreamHub({
          campaignUuid: persisted.campaignUuid,
          tenantId: tenant,
          token: bareToken,
          lastEventId: '0',
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readPersistedCampaignState, staticTenantId, bareToken]);

  const closeBuyModal = () => {
    setShowBuyModal(false);
  };

  const closeConfigModal = () => {
    setShowConfigModal(false);
    window.location.reload();
  };

  const closeDocumentationModal = () => {
    setShowDocumentationModal(false);
  };

  const closeFundsModal = () => {
    setShowFundsModal(false);
  };

  const showDeleteModal = () => {
    setDeleteModal(true);
  };

  const hideDeleteModal = () => {
    setDeleteModal(false);
  };

  const brand = (
    <span className="navbar-logo">
      <Logo />
    </span>
  );
  const reader = getConfig("customConfig.readerImage");

  const [defaultSection] = sections;

  useEffect(() => {
    let timer;
    if (isFirstLogin && isFirstLoginBuyReaderModalEnabled) {
      timer = setTimeout(() => {
        dispatch(setFirstLogin(false));
        setShowBuyModal(true);
      }, 1000);
    }
    return () => timer && clearTimeout(timer);
  }, [isFirstLogin, isFirstLoginBuyReaderModalEnabled, dispatch]);

  useEffect(() => {
    const customPageTitle = getConfig("customConfig.pageTitle");
    if (customPageTitle) {
      document.title = customPageTitle;
    }
  }, []);

  if (!userPermissions.length || !featureFlags.length)
    return <></>;

  return (
    <div>
      {!isAuthenticated ? (
        <Redirect to="/" />
      ) : (
        <ModalContext.Provider
          value={{
            openModal: openModal,
            closeModal: closeModal,
          }}
        >
          <div className="dashboard-main">
            <DashboardHeader
              openModal={openModal}
              closeModal={closeModal}
              brand={brand}
              home="/"
            />
            <FloatingNotifications
              visible={showProcessingPopup}
              onDismiss={() => {
                // Si el usuario cierra manualmente la ventana en estado final, marcar acknowledged
                try {
                  const tenant = sanitizeTenantId(staticTenantId);
                  if (tenant) {
                    const persisted = readPersistedCampaignState(tenant) || {};
                    if (uiCompleted) {
                      persistCampaignState(tenant, { ...persisted, completed: true, acknowledged: true });
                    }
                  }
                } catch {}
                setShowProcessingPopup(false);
              }}
              fileName={campaignFileName} // meta de la campaña en proceso
              totalRegisters={campaignTotal} // meta de la campaña en proceso
              successCount={
                uiCompleted && rehydratedSuccess !== null
                  ? rehydratedSuccess
                  : sseSuccessCount
              }
              failedCount={
                uiCompleted && rehydratedFailed !== null
                  ? rehydratedFailed
                  : sseFailedCount
              }
              completed={uiCompleted}
              lastError={sseLastError}
            />

            {user && ConfigurationModal && (
              <ConfigurationModal
                user={user}
                token={token}
                showModal={deleteModal}
                showDeleteModal={showDeleteModal}
                hideModal={hideDeleteModal}
                closeConfigModal={closeConfigModal}
                isOpen={showConfigModal}
                dispatch={dispatch}
              />
            )}

            {user && DocumentationModal && (
              <DocumentationModal
                user={user}
                token={token}
                showModal={deleteModal}
                showDeleteModal={showDeleteModal}
                hideModal={hideDeleteModal}
                closeDocumentationModal={closeDocumentationModal}
                isOpen={showDocumentationModal}
                dispatch={dispatch}
              />
            )}

            {user && user.retirementFunds && (
              <Modal
                isOpen={showFundsModal}
                toggle={() => closeFundsModal()}
                size="lg"
                className="dashboard-modal config-modal"
              >
                <ModalHeader>
                  {__("dashboard.components.modal.funds")}
                </ModalHeader>
                <ModalBody>
                  <Row className="mb-5">
                    <Col>{__("dashboard.components.modal.depositMoney")}</Col>
                  </Row>
                  <Row>
                    <Col>
                      <span className="dashboard-modal-subtitle">
                        {userFundsDefaultValues.title}
                      </span>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="dashboard-card-item">
                        <div className="dashboard-card-item-body color-gray">
                          {Object.entries(userFundsDefaultValues.data).map(
                            ([key, value]) => (
                              <Row key={key}>
                                <Col sm="3">
                                  <span className="strong--500">
                                    {key + ": "}
                                  </span>
                                </Col>
                                <Col sm="9">{value.toString()}</Col>
                              </Row>
                            )
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <hr className="mt-5" />
                </ModalBody>
                <ModalFooter>
                  <Button color="secondary" onClick={() => closeFundsModal()}>
                    {__("actions.close")}
                  </Button>
                </ModalFooter>
              </Modal>
            )}

            <Modal
              isOpen={showBuyModal}
              toggle={() => closeBuyModal()}
              className="dashboard-center-modal modal-lg"
            >
              <ModalHeader style={{ padding: "15px 0" }} />
              <ModalBody>
                <img src={reader} alt="reader" />
                <h2>{__("dashboard.sections.home.buyProduct")}</h2>
                <span>{__.bb("dashboard.sections.home.indicationsUse")}</span>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={() => closeBuyModal()}>
                  {__("dashboard.sections.home.iHaveOne")}
                </Button>
                <a href={buyReaderUrl}>
                  <Button color="primary">
                    {__("dashboard.sections.home.buyNow")}
                  </Button>
                </a>
              </ModalFooter>
            </Modal>
            {showBankAccountModal && (
              <BankAccountModal
                isOpen={showBankAccountModal}
                toggle={() => {
                  closeModal("BankAccountModal");
                }}
              />
            )}
            {showReadersModal && (
              <ReadersModal
                isOpen={showReadersModal}
                toggle={() => {
                  closeModal('ReadersModal');}}
              />
            )}
            {showHelpModal && (
              <HelpModal 
                isOpen={showHelpModal}
                toggle={() => {
                  closeModal("HelpModal");
                }}
              />
            )}
            {showSubsidiariesModal && (
              <SubsidiariesModal
                isOpen={showSubsidiariesModal}
                toggle={() => {
                  closeModal("SubsidiariesModal");
                }}
              />
            )}
            {showCourierTrackingModal && (
              <CourierTrackingModal
                isOpen={showCourierTrackingModal}
                toggle={() => {
                  closeModal("CourierTrackingModal");
                }}
              />
            )}
            {showStaticQRCodeModal && (
              <StaticQRCodeModal
                isOpen={showStaticQRCodeModal}
                toggle={() => {
                  closeModal("StaticQRCodeModal");
                }}
              />
            )}
            {showMassivePaymentLinkModal && (
              <MassivePaymentLinkModal
                isOpen={showMassivePaymentLinkModal}
                toggle={() => {
                  closeModal("MassivePaymentLinkModal");
                }}
                token={token}
                subsidiaryId={user?.subsidiaryId}
                onCampaignSubscribe={handleCampaignSubscribe}
                onCampaignConfirmed={handleCampaignConfirmed}
                onConfirmSuccess={() => setShowProcessingPopup(true)}
                onCampaignReset={handleCampaignReset}
                hasActiveCampaign={Boolean(isStreamEnabled)}
              />
            )}
            <PaymentLinkModal
              isOpen={showPaymentLinkModal}
              toggle={() => {
                closeModal("PaymentLinkModal");
              }}
            />
            {showImportModal && (
              <ImportModal
                showImport={showImportModal}
                onClose={() => {
                  closeModal("ImportModal");
                }}
                token={token}
                subsidiaryId={user?.subsidiaryId}
              />
            )}
            {!!sections.length && (
              <div className="dashboard-content">
                <Switch>
                  {sections.map((section, index) => (
                    <Route
                      key={index}
                      exact={section.exact}
                      path={section.path}
                      render={(props) => (
                        <DashboardSection section={section} {...props} />
                      )}
                    />
                  ))}
                  <Redirect exact to={defaultSection.path} />
                </Switch>
              </div>
            )}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </div>
        </ModalContext.Provider>
      )}
      
      {/* Notificaciones flotantes globales */}
      {/* The original code had floating notifications here, but they are now rendered inside the ModalContext.Provider */}
    </div>
  );
};
